// =============================================================================
// E-Signature Admin API Service
// =============================================================================
// Handles admin-facing e-sign API calls for templates and envelopes.
// Uses the shared authenticated api instance.
// =============================================================================

import api from './api';
import type {
  SignatureTemplate,
  SignatureTemplateField,
  SignatureEnvelope,
  SignatureAuditEvent,
  CreateTemplateInput,
  CreateTemplateFieldInput,
  CreateEnvelopeInput,
} from '../types/esign';

interface PaginatedResponse<T> {
  data: T[];
  meta: {
    pagination: {
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
      hasMore: boolean;
    };
  };
}

const esignService = {
  // ===========================================================================
  // Templates
  // ===========================================================================

  async createTemplate(input: CreateTemplateInput, file: File): Promise<SignatureTemplate> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('name', input.name);
    formData.append('documentType', input.documentType);
    if (input.description) formData.append('description', input.description);

    const response = await api.post('/esign/templates', formData);
    return response.data.data;
  },

  async getTemplate(templateId: string): Promise<SignatureTemplate> {
    const response = await api.get(`/esign/templates/${templateId}`);
    return response.data.data;
  },

  async listTemplates(params?: {
    page?: number;
    pageSize?: number;
    documentType?: string;
    isActive?: boolean;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<PaginatedResponse<SignatureTemplate>> {
    const response = await api.get('/esign/templates', { params });
    return { data: response.data.data, meta: response.data.meta };
  },

  async updateTemplate(templateId: string, input: Partial<CreateTemplateInput & { isActive: boolean }>): Promise<SignatureTemplate> {
    const response = await api.put(`/esign/templates/${templateId}`, input);
    return response.data.data;
  },

  async deleteTemplate(templateId: string): Promise<void> {
    await api.delete(`/esign/templates/${templateId}`);
  },

  async getTemplateDocumentUrl(templateId: string): Promise<string> {
    const response = await api.get(`/esign/templates/${templateId}/document-url`);
    return response.data.data.url;
  },

  // ===========================================================================
  // Template Fields
  // ===========================================================================

  async getTemplateFields(templateId: string): Promise<SignatureTemplateField[]> {
    const response = await api.get(`/esign/templates/${templateId}/fields`);
    return response.data.data;
  },

  async addTemplateField(templateId: string, input: CreateTemplateFieldInput): Promise<SignatureTemplateField> {
    const response = await api.post(`/esign/templates/${templateId}/fields`, input);
    return response.data.data;
  },

  async updateTemplateField(templateId: string, fieldId: string, input: Partial<CreateTemplateFieldInput>): Promise<SignatureTemplateField> {
    const response = await api.put(`/esign/templates/${templateId}/fields/${fieldId}`, input);
    return response.data.data;
  },

  async deleteTemplateField(templateId: string, fieldId: string): Promise<void> {
    await api.delete(`/esign/templates/${templateId}/fields/${fieldId}`);
  },

  async batchUpdateFieldPositions(templateId: string, updates: Array<{
    fieldId: string;
    xPercent: number;
    yPercent: number;
    widthPercent?: number;
    heightPercent?: number;
  }>): Promise<SignatureTemplateField[]> {
    const response = await api.put(`/esign/templates/${templateId}/fields-batch`, { updates });
    return response.data.data;
  },

  // ===========================================================================
  // Envelopes
  // ===========================================================================

  async createEnvelope(input: CreateEnvelopeInput, file?: File): Promise<SignatureEnvelope> {
    if (file) {
      const formData = new FormData();
      formData.append('document', file);
      Object.entries(input).forEach(([key, value]) => {
        if (value !== undefined) {
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        }
      });
      const response = await api.post('/esign/envelopes', formData);
      return response.data.data;
    }
    const response = await api.post('/esign/envelopes', input);
    return response.data.data;
  },

  async getEnvelope(envelopeId: string): Promise<SignatureEnvelope> {
    const response = await api.get(`/esign/envelopes/${envelopeId}`);
    return response.data.data;
  },

  async listEnvelopes(params?: {
    page?: number;
    pageSize?: number;
    status?: string | string[];
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<PaginatedResponse<SignatureEnvelope>> {
    const response = await api.get('/esign/envelopes', { params });
    return { data: response.data.data, meta: response.data.meta };
  },

  async sendEnvelope(envelopeId: string): Promise<{ id: string; status: string; sentAt: string }> {
    const response = await api.post(`/esign/envelopes/${envelopeId}/send`, { sendNow: true });
    return response.data.data;
  },

  async voidEnvelope(envelopeId: string, reason: string, notifySigners = true): Promise<SignatureEnvelope> {
    const response = await api.post(`/esign/envelopes/${envelopeId}/void`, { reason, notifySigners });
    return response.data.data;
  },

  async sendReminder(envelopeId: string, signerId?: string, customMessage?: string): Promise<any> {
    const response = await api.post(`/esign/envelopes/${envelopeId}/remind`, { signerId, customMessage });
    return response.data.data;
  },

  async getAuditTrail(envelopeId: string, page = 1, pageSize = 50): Promise<{
    envelopeId: string;
    totalEvents: number;
    events: SignatureAuditEvent[];
  }> {
    const response = await api.get(`/esign/envelopes/${envelopeId}/audit`, { params: { page, pageSize } });
    return response.data.data;
  },

  async verifyAuditTrail(envelopeId: string): Promise<{ valid: boolean; eventCount: number; brokenAtIndex?: number }> {
    const response = await api.get(`/esign/envelopes/${envelopeId}/audit/verify`);
    return response.data.data;
  },

  async downloadDocument(envelopeId: string, type: 'original' | 'signed' | 'certificate' = 'signed'): Promise<Blob> {
    const response = await api.get(`/esign/envelopes/${envelopeId}/document`, {
      params: { type },
      responseType: 'blob',
    });
    return response.data;
  },
};

export default esignService;
